import { useCallback, useContext, useEffect, useMemo, useRef, type MouseEvent, type ReactNode } from 'react'
import clsx from 'clsx'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { v4 as uuidv4 } from 'uuid'
import Clickable from 'components/Common/Clickable/Clickable'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import type { VimeoPlayerOptions } from 'components/Common/Video/VimeoVideo'
import VimeoVideo from 'components/Common/Video/VimeoVideo'
import ModalContext from 'contexts/ModalContext'
import ImageGalleryLightbox from 'LuxKit/Carousel/ImageGalleryLightbox'
import VideoLightbox from 'LuxKit/Carousel/VideoLightbox'
import TextButton from 'LuxKit/Button/TextButton'
import SolidPlayIcon from 'LuxKit/Icons/solid/SolidPlayIcon'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import NeutralLabel from 'LuxKit/Label/NeutralLabel'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import LtleTooltipWrapper from 'components/Common/Labels/LtleTooltipWrapper'
import Group from 'components/utils/Group'
import { OFFER_TYPE_HOTEL } from 'constants/offer'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import { carouselImageImpressionEvent } from 'analytics/snowplow/events'
import { trackEvent } from 'analytics/analytics'
import { EmptyArray } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  grid-template-rows: minmax(${rem(324)}, 1fr);
  gap: ${rem(8)};
  width: 100%;
  flex: 1;
  min-height: ${rem(324)};
`

const MainTile = styled.div`
  position: relative;
  overflow: hidden;
  height: 100%;
  border-radius: ${props => props.theme.borderRadius.M};

  &.full-width {
    grid-column: 1 / -1;
  }
`

const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  height: 100%;
  min-height: 0;
`

const SideTile = styled.div`
  position: relative;
  overflow: hidden;
  flex: 1;
  border-radius: ${props => props.theme.borderRadius.M};
  min-height: ${rem(158)};
`

const ImageHitArea = styled(Clickable)`
  position: absolute;
  inset: 0;
  cursor: pointer;
`

const FillImage = styled(ResponsiveImage)`
  position: absolute;
  inset: 0;
`

const VideoFill = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-color: ${props => props.theme.palette.neutral.surface.darkBackground};

  > * {
    pointer-events: none;
  }
`

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: ${rem(20)};
  pointer-events: none;
`

const InteractiveControl = styled.div`
  pointer-events: auto;
`

const HeroLabels = styled(LabelGroup)`
  position: absolute;
  top: ${rem(16)};
  inset-inline-start: ${rem(16)};
  z-index: 1;
  pointer-events: auto;
`

interface Props {
  offer: App.HotelOffer
  charterBundleRoute?: App.CharterBundleRoute
  autoPlayVideo?: boolean
  onTopHeroImageLoad?: () => void
}

const HERO_IMAGE_PROPS: ImageParams = { tabletWidth: '800px', quality: 'good' }
const SIDE_IMAGE_PROPS: ImageParams = { tabletWidth: '400px', quality: 'good' }
const HERO_PRIORITY_PROPS = { fetchPriority: 'high' as const, disablePlaceholder: true }
const OFFER_TOP_IMAGE_PERF_ATTR = { 'data-perf': 'offer-top-image' }
const GALLERY_IMAGE_LIST = 'offer-page-gallery'
const loopQuery: VimeoPlayerOptions = { loop: true, autoplay: true, controls: true }
const mutedLoopQuery: VimeoPlayerOptions = { muted: true, loop: true, controls: true }
const heroVideoQuery: VimeoPlayerOptions = { ...mutedLoopQuery, autoplay: true, playsinline: true }

function FlashDesktopOfferHeaderGallery(props: Props) {
  const { offer, charterBundleRoute, autoPlayVideo = false, onTopHeroImageLoad = noop } = props
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const images = offer.images
  const heroImage = images[0]
  const sideImages = useMemo(() => images.slice(1, 3), [images])
  const videoId = offer.vimeoVideoId
  const hasVideoSlide = !!videoId
  const listId = useMemo(() => uuidv4(), [])
  const videoRef = useRef<any>(null)

  useEffect(() => {
    if (autoPlayVideo && videoId) {
      videoRef.current?.play('carousel')
    }
  }, [autoPlayVideo, videoId])

  const videoInterstitials = useMemo(() => {
    if (!hasVideoSlide || !videoId) {
      return EmptyArray
    }

    return [{
      id: `video-${videoId}`,
      position: 2,
      element: <VimeoVideo vimeoVideoId={videoId} query={mutedLoopQuery} />,
    }]
  }, [hasVideoSlide, videoId])

  const handleImageChange = useCallback((index: number, image?: App.Image) => {
    if (image) {
      trackEvent(carouselImageImpressionEvent(image, index + 1, GALLERY_IMAGE_LIST, listId, offer))
    }
  }, [listId, offer])

  const openGalleryFromImage = useCallback((event: MouseEvent<HTMLElement>) => {
    const imageIndex = Number(event.currentTarget.dataset.index) || 0
    const lightboxIndex = hasVideoSlide && imageIndex > 0 ? imageIndex + 1 : imageIndex
    showModal(<ImageGalleryLightbox
      images={images}
      defaultIndex={lightboxIndex}
      interstitials={videoInterstitials}
      onImageChange={handleImageChange}
    />)
  }, [handleImageChange, hasVideoSlide, images, showModal, videoInterstitials])

  const showVideoLightbox = useCallback(() => {
    if (videoId) {
      showModal(<VideoLightbox videoId={videoId} playOnMount query={loopQuery} />)
    }
  }, [showModal, videoId])

  const galleryLabel = intl.formatMessage({
    id: 'TKRV3zsVkDdy046qshY5',
    defaultMessage: 'Gallery',
    description: 'Button on the desktop flash offer header that opens the full image gallery',
  })
  const playVideoLabel = intl.formatMessage({
    id: 'z6jPA9lfWeT2PbwWg6co',
    defaultMessage: 'Play video',
    description: 'Button on the desktop flash offer header that plays the property video',
  })
  const charterBundleLabel = intl.formatMessage({
    id: 'okIkBba97k3jYlquar_m',
    defaultMessage: 'Hotel & Flight bundle',
    description: 'Label for hotel and flight bundle offers in the flights flow',
  })
  let productType: App.ProductType | undefined = offer.productType
  if (charterBundleRoute) {
    productType = undefined
  } else if (isLPCTacticalEnabled() && offer.hasTactical) {
    productType = 'limited_time_special'
  } else if (offer.isPartnerProperty) {
    productType = undefined
  }
  let productTypeLabel: ReactNode
  if (productType === OFFER_TYPE_HOTEL) {
    productTypeLabel = (
      <LtleTooltipWrapper>
        <ProductTypeLabel productType={productType} />
      </LtleTooltipWrapper>
    )
  } else if (productType) {
    productTypeLabel = <ProductTypeLabel productType={productType} />
  }

  if (!heroImage) {
    return null
  }

  return (
    <GalleryGrid>
      <MainTile className={clsx({ 'full-width': sideImages.length === 0 })}>
        {heroImage.id && (
          <FillImage
            id={heroImage.id}
            fit="center"
            objectFit="cover"
            alt={offer.property.name}
            {...HERO_IMAGE_PROPS}
            {...HERO_PRIORITY_PROPS}
            {...OFFER_TOP_IMAGE_PERF_ATTR}
            onLoad={onTopHeroImageLoad}
          />
        )}
        {autoPlayVideo && videoId && (
          <VideoFill>
            <VimeoVideo ref={videoRef} vimeoVideoId={videoId} query={heroVideoQuery} />
          </VideoFill>
        )}
        <ImageHitArea aria-label={galleryLabel} data-index={0} onClick={openGalleryFromImage} />
        {(productTypeLabel || charterBundleRoute) && (
          <HeroLabels>
            {productTypeLabel}
            {charterBundleRoute && <NeutralLabel>{charterBundleLabel}</NeutralLabel>}
          </HeroLabels>
        )}
        <Overlay>
          <Group direction="horizontal" horizontalAlign="end" verticalAlign="end">
            {hasVideoSlide && (
              <InteractiveControl>
                <TextButton kind="primary" variant="dark" size="small" startIcon={<SolidPlayIcon />} onClick={showVideoLightbox}>
                  {playVideoLabel}
                </TextButton>
              </InteractiveControl>
            )}
          </Group>
        </Overlay>
      </MainTile>
      {sideImages.length > 0 && (
        <SideColumn>
          {sideImages.map((image, index) => (
            <SideTile key={image.id}>
              {image.id && (
                <FillImage
                  id={image.id}
                  fit="center"
                  objectFit="cover"
                  alt={offer.property.name}
                  {...SIDE_IMAGE_PROPS}
                />
              )}
              <ImageHitArea aria-label={galleryLabel} data-index={index + 1} onClick={openGalleryFromImage} />
            </SideTile>
          ))}
        </SideColumn>
      )}
    </GalleryGrid>
  )
}

export default FlashDesktopOfferHeaderGallery
